import type { ApolloError } from '@apollo/client';
import type { FieldValues, Path } from 'react-hook-form';
import { object, string } from 'yup';

type RawApiError = {
  path: string;
  message: string;
};

const rawApiErrorSchema = object({
  path: string().required(),
  message: string().required(),
});

const isRawApiError = (err: unknown): err is RawApiError => rawApiErrorSchema.isValidSync(err);

export type ParsedFieldError<TFieldId extends string> = {
  fieldId: TFieldId;
  message: string;
};

export type ParsedApolloFieldErrors<TFieldId extends string> = {
  fieldErrors: ParsedFieldError<TFieldId>[];
  generalErrors: string[];
};

/** Forces excess keys (not in TAllowed) to `never`, producing a compile error. */
type RejectExtraKeys<TMap, TAllowed extends string> = Record<Exclude<keyof TMap & string, TAllowed>, never>;

/**
 * Creates a type-safe mapping from API response field paths (keys) to form state paths (values).
 * - Keys are autocompleted from `Path<TResponse>` and strictly validated (excess keys are rejected).
 * - Values are constrained to `Path<TFormState>` (supports nested paths and union form states).
 * - Both generic parameters are required; omitting them defaults to `never` which errors on every entry.
 */
export const createFieldPathMap =
  <TResponse extends FieldValues = never, TFormState extends FieldValues = never>() =>
  <const TMap extends Partial<Record<Path<TResponse>, Path<TFormState>>>>(
    map: TMap & RejectExtraKeys<TMap, Path<TResponse>>
  ): TMap =>
    map;

export const parseApolloFieldErrors = <const TMap extends Partial<Record<string, string>>>(
  error: Pick<ApolloError, 'graphQLErrors'>,
  pathMap: TMap
): ParsedApolloFieldErrors<NonNullable<TMap[keyof TMap]>> => {
  type FormFieldKey = NonNullable<TMap[keyof TMap]>;

  const rawErrors = (error.graphQLErrors ?? [])
    .flatMap(gqlErr => (Array.isArray(gqlErr.extensions?.errors) ? gqlErr.extensions.errors : []))
    .filter(isRawApiError);

  const fieldErrors: ParsedFieldError<FormFieldKey>[] = [];
  const generalErrors: string[] = [];

  for (const { path, message } of rawErrors) {
    const fieldId = pathMap[path];

    if (fieldId !== undefined) {
      fieldErrors.push({ fieldId: fieldId as FormFieldKey, message });
    } else {
      generalErrors.push(message);
    }
  }

  return { fieldErrors, generalErrors };
};
